import React, { useState } from 'react';
import { Loader2, Save, X } from 'lucide-react';
import { SifaTaetigkeit, TAETIGKEIT_ARTEN, TaetigkeitArt } from '../../services/sifa/betreuungTypes';
import { createTaetigkeit, updateTaetigkeit } from '../../services/sifa/sifaBetreuungService';
import { formatDauer, parseDauer } from '../../services/sifa/taetigkeitenLogic';
import { describeError } from '../../services/errorMessage';
import { formatDatum, heuteIso } from './unterweisungModulLogic';
import {
  TaetigkeitEntwurf, entwurfAusTaetigkeit, entwurfZuNeueTaetigkeit, leererTaetigkeitEntwurf, pruefeTaetigkeitEntwurf,
} from './taetigkeitenModulLogic';

interface Props {
  projectId: string;
  /** null = neu erfassen. */
  taetigkeit: SifaTaetigkeit | null;
  /** Anzeigename des angemeldeten Nutzers — Vorbelegung fuer „Durchgeführt von". */
  nutzerName: string | null;
  isReadOnly?: boolean;
  onClose: () => void;
  onGespeichert: (t: SifaTaetigkeit) => void;
}

const FELD = 'w-full px-3 py-2 min-h-[40px] text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-indigo-300 disabled:text-slate-500';
const LABEL = 'block text-[11px] font-bold uppercase text-slate-500 mb-1';

/**
 * Erfassen/Bearbeiten einer Taetigkeit. Die Dauer ist ein Textfeld: „1:30"
 * oder „90" — parseDauer (Paket C) macht Minuten daraus, die Vorschau
 * daneben zeigt, was verstanden wurde. Eine abgerechnete Zeile
 * (gebuchtAm) bleibt Beleg und ist hier nur noch anzusehen.
 */
export const TaetigkeitDialog: React.FC<Props> = ({ projectId, taetigkeit, nutzerName, isReadOnly = false, onClose, onGespeichert }) => {
  const gesperrt = isReadOnly || !!taetigkeit?.gebuchtAm;
  const [e, setE] = useState<TaetigkeitEntwurf>(() => (taetigkeit
    ? entwurfAusTaetigkeit(taetigkeit, formatDauer(taetigkeit.dauerMinuten))
    : leererTaetigkeitEntwurf(heuteIso(), nutzerName)));
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string[]>([]);

  const setze = <K extends keyof TaetigkeitEntwurf>(k: K, v: TaetigkeitEntwurf[K]) => setE((prev) => ({ ...prev, [k]: v }));
  const minuten = parseDauer(e.dauer);

  const speichern = async () => {
    if (gesperrt || speichert) return;
    const probleme = pruefeTaetigkeitEntwurf(e, minuten);
    if (probleme.length > 0 || minuten === null) { setFehler(probleme); return; }
    setSpeichert(true);
    setFehler([]);
    try {
      const eingabe = entwurfZuNeueTaetigkeit(e, minuten);
      const gespeichert = taetigkeit ? await updateTaetigkeit(taetigkeit.id, eingabe) : await createTaetigkeit(projectId, eingabe);
      onGespeichert(gespeichert);
    } catch (err) {
      setFehler([describeError(err)]);
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] flex flex-col" onClick={(ev) => ev.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <h3 className="text-sm font-bold text-slate-800">{taetigkeit ? (gesperrt ? 'Tätigkeit' : 'Tätigkeit bearbeiten') : 'Tätigkeit erfassen'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Datum</label>
              <input type="date" value={e.datum} onChange={(ev) => setze('datum', ev.target.value)} disabled={gesperrt} className={FELD} />
            </div>
            <div>
              <label className={LABEL}>Dauer</label>
              <input value={e.dauer} onChange={(ev) => setze('dauer', ev.target.value)} disabled={gesperrt} className={FELD} placeholder="1:30 oder 90" inputMode="decimal" />
              <p className="text-[11px] text-slate-500 mt-1">{minuten !== null && minuten > 0 ? formatDauer(minuten) : 'Minuten, 1:30 oder 1,5 h'}</p>
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Art (DGUV V2 Anlage 3)</label>
              <select value={e.art} onChange={(ev) => setze('art', ev.target.value as TaetigkeitArt)} disabled={gesperrt} className={FELD}>
                {TAETIGKEIT_ARTEN.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Titel</label>
              <input value={e.titel} onChange={(ev) => setze('titel', ev.target.value)} disabled={gesperrt} className={FELD} placeholder="z. B. Begehung Lager und Werkstatt" autoFocus={!taetigkeit} />
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Beschreibung</label>
              <textarea value={e.beschreibung} onChange={(ev) => setze('beschreibung', ev.target.value)} disabled={gesperrt} rows={3} className={`${FELD} min-h-[72px]`} />
            </div>
            <div>
              <label className={LABEL}>Bezug</label>
              <input value={e.bezug} onChange={(ev) => setze('bezug', ev.target.value)} disabled={gesperrt} className={FELD} placeholder="Bericht Nr. / Unterweisung" />
            </div>
            <div>
              <label className={LABEL}>Durchgeführt von</label>
              <input value={e.durchgefuehrtVon} onChange={(ev) => setze('durchgefuehrtVon', ev.target.value)} disabled={gesperrt} className={FELD} />
            </div>
          </div>

          {taetigkeit?.gebuchtAm && (
            <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              Abgerechnet am {formatDatum(taetigkeit.gebuchtAm)} — diese Zeile ist Beleg und wird nicht mehr geändert.
            </p>
          )}
          {fehler.length > 0 && (
            <ul className="text-xs text-red-700 space-y-0.5">{fehler.map((f) => <li key={f}>{f}</li>)}</ul>
          )}
        </div>
        {!gesperrt && (
          <div className="p-4 border-t border-slate-200 flex justify-end gap-2 shrink-0">
            <button onClick={onClose} className="min-h-[40px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl">Abbrechen</button>
            <button onClick={() => void speichern()} disabled={speichert} className="min-h-[40px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl disabled:opacity-40">
              {speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
